import { Controller } from '@hotwired/stimulus';

/**
 * Récapitulatif LIVE de la modale « Répartir le solde entre les entreprises »
 * (DGD, F11, mockup 09 panneau 3).
 *
 * Réécrit DEUX nombres pendant la frappe : « Réparti aux sous-traitants » et
 * « Reste au titulaire ». Rien d'autre.
 *
 * ── AFFICHAGE SEUL (décision PINNÉE) ──────────────────────────────────────
 *
 * CE CONTRÔLEUR N'EST PAS UNE GARDE. Il ne valide rien, ne bloque rien, ne
 * soumet rien, et n'empêche aucune saisie. Les deux vraies gardes — plafond
 * agréé DC4 et ventilation excédant le solde — vivent dans
 * `app/services/dgd/subcontract_payments_writer.rb`, sous son verrou, et
 * refusent la sauvegarde en rendant `dgds/_subcontract_split_modal_error`.
 * Un POST forgé ne voit jamais ce fichier. Même régime que
 * `subcontract_cap_controller.js` : le serveur refuse, le JS ne fait que
 * donner à lire.
 *
 * Corollaire assumé : `data-cap-cents` (plafond agréé) et `data-paid-cents`
 * (déjà payé), portés par chaque champ, ne sont **PAS LUS ICI**. Ils sont
 * rendus par le serveur en centimes entiers pour un éventuel rappel visuel
 * ultérieur ; les lire pour refuser une saisie serait réimplémenter la garde
 * de plafond en JS, ce que l'arbitrage du lot interdit explicitement.
 *
 * ── AUCUN STYLE NI LIBELLÉ ÉCRIT ICI ──────────────────────────────────────
 *
 * Le JS échappe à `ui-lint-erb.sh` : tout ce qui est du design system doit
 * donc rester côté serveur. Ce fichier n'écrit QUE des nombres formatés dans
 * les deux cibles. Les libellés (« Réparti aux sous-traitants »), les unités
 * (« HT », « TTC »), les classes et l'`aria-live="polite"` appartiennent au
 * partial `app/views/dgds/_subcontract_split_modal.html.erb`, qui les rend au
 * premier paint avec la valeur PERSISTÉE : sans JS, ou avant `connect`, le
 * bloc est déjà juste.
 *
 * ── Les deux miroirs du serveur ───────────────────────────────────────────
 *
 * - `distributed` est le miroir de `DgdsHelper#format_negative_currency` : le
 *   montant réparti est une DÉDUCTION du solde, donc il se lit signé négatif
 *   — SAUF à zéro, qui se rend « 0,00 € » NON SIGNÉ. C'est l'état par défaut
 *   de la modale (aucune répartition saisie), donc celui que l'architecte
 *   voit le plus souvent : `Intl` rendrait « -0,00 € » sur un `-0`, et le
 *   premier paint divergerait dès la première frappe annulée.
 * - `remainder` = solde − réparti, la soustraction des DEUX opérandes DÉJÀ
 *   AFFICHÉS au-dessus (miroir de `remainder = balance - allocated_total`
 *   dans le partial, lui-même miroir de `DgdCalculator`). Jamais une
 *   troisième source de vérité.
 *
 * Les centimes sont ENTIERS des deux côtés (régime `subcontract_cap_controller.js`) :
 * on additionne et on soustrait des entiers, jamais des flottants, pour qu'un
 * cumul à l'euro près ne bascule pas sur une erreur d'arrondi binaire.
 *
 * Aucun écouteur n'est posé hors de `this.element` (les `input` passent par
 * `data-action`), donc il n'y a rien à retirer : la symétrie `connect` /
 * `disconnect` est tenue par l'absence des deux côtés.
 *
 * Targets:
 * - amount (N): les champs de saisie, en euros. Portent aussi `data-cap-cents`
 *   et `data-paid-cents`, délibérément non lus (voir ci-dessus).
 * - distributed (1): cible d'écriture de « Réparti aux sous-traitants ».
 * - remainder (1): cible d'écriture de « Reste au titulaire ».
 *
 * ── Fermeture de la modale après un enregistrement réussi (défaut D1) ─────
 *
 * MÊME MÉCANISME QUE `dgd_retention_modal_controller.js`, la modale voisine de
 * la même page — qui, elle, se fermait déjà : on écoute `turbo:submit-end` sur
 * LE FORMULAIRE et on ferme sur `event.detail.success`. Un refus (422) ne porte
 * pas `success`, donc la modale reste ouverte avec la saisie intacte et le
 * message du writer — c'est le comportement voulu, pas un oubli.
 *
 * Deux écarts assumés avec le précédent, tous deux dans le sens du contrat
 * maison :
 *   - l'écouteur passe par `data-action` plutôt que par un couple
 *     `addEventListener` / `removeEventListener` posé à la main. L'événement
 *     est émis sur `this.element` (le formulaire), donc Stimulus le câble et
 *     le décâble tout seul : la symétrie `connect`/`disconnect` est tenue par
 *     l'absence des deux côtés.
 *   - l'id de la modale vient du SERVEUR (`modalIdValue`, alimenté par
 *     `DgdsHelper::SUBCONTRACT_SPLIT_MODAL_DOM_ID`) et n'est pas codé en dur
 *     ici : les deux ne peuvent pas diverger.
 *
 * INTERACTION AVEC D2. Depuis le même lot, le stream de succès replace AUSSI
 * l'hôte de la modale (4e frère de l'Acte 3), et le nœud neuf arrive `hidden`.
 * Les deux voies convergent donc vers « modale fermée », dans n'importe quel
 * ordre et sans se gêner : si le replace gagne la course, ce contrôleur est
 * déjà détaché et n'a rien à faire ; s'il perd, `close()` s'applique à un nœud
 * qui sera remplacé juste après. Ne pas retirer l'une en croyant l'autre
 * suffisante — le replace ne ferme que parce qu'il est là, et cette fermeture-ci
 * est la seule qui reste vraie si un jour le stream cesse de porter la modale.
 *
 * Values:
 * - balanceCents (Number): le solde à régler, TTC, en CENTIMES ENTIERS.
 * - modalId (String): l'id DOM de la modale hôte, à fermer après un succès.
 *
 * Actions:
 * - recompute: recalcule les deux nombres. Câblé sur `input` de chaque champ,
 *   et appelé une fois au `connect`.
 * - closeOnSuccess: ferme la modale sur un `turbo:submit-end` réussi.
 *
 * Contrat serveur → JS verrouillé par
 * `test/integration/dgd_subcontract_split_modal_test.rb` § 5.
 *
 * @example
 * <form data-controller="dgd-allocation"
 *       data-action="turbo:submit-end->dgd-allocation#closeOnSuccess"
 *       data-dgd-allocation-balance-cents-value="5500000"
 *       data-dgd-allocation-modal-id-value="dgd-subcontract-split-modal">
 *   <input type="number"
 *          data-dgd-allocation-target="amount"
 *          data-action="input->dgd-allocation#recompute"
 *          data-cap-cents="4500000"
 *          data-paid-cents="1200000">
 *   <span data-dgd-allocation-target="distributed">0,00 €</span> HT
 *   <span data-dgd-allocation-target="remainder">55 000,00 €</span> TTC
 * </form>
 */
export default class extends Controller {
  static targets = ['amount', 'distributed', 'remainder'];

  static values = { balanceCents: Number, modalId: String };

  connect() {
    // Le serveur a peint les bonnes valeurs (données PERSISTÉES) et les champs
    // rouvrent sur cette même répartition : ce premier passage est donc un
    // no-op visuel dans le cas nominal. Il existe pour le cas où le champ
    // porte déjà autre chose que le persisté (retour navigateur, autofill).
    this.recompute();
  }

  recompute() {
    // Sans le solde, ou sans les deux cibles, on NE TOUCHE À RIEN : mieux vaut
    // laisser le rendu serveur en place qu'écrire un « NaN € » par-dessus.
    // `hasBalanceCentsValue` est faux quand l'attribut est absent — la valeur
    // par défaut d'un `Number` étant 0, sans ce garde-fou un attribut oublié
    // afficherait silencieusement un solde nul au lieu de ne rien faire.
    if (!this.hasBalanceCentsValue) return;
    if (!this.hasDistributedTarget || !this.hasRemainderTarget) return;

    const distributedCents = this.amountTargets.reduce(
      (total, input) => total + this.#toCents(input.value),
      0
    );

    this.distributedTarget.textContent = this.#formatDeduction(distributedCents);
    this.remainderTarget.textContent = this.#format(this.balanceCentsValue - distributedCents);
  }

  // D1 — la modale ne se fermait PAS après un enregistrement réussi : le flash
  // s'affichait, le tableau et le bandeau se mettaient à jour DERRIÈRE, et la
  // modale restait par-dessus. Reproduit 3 fois par la QA navigateur.
  //
  // `event.target !== this.element` : `turbo:submit-end` bulle, donc un
  // formulaire imbriqué (aucun aujourd'hui, mais la modale de retenue se garde
  // déjà de la même façon) ne doit pas fermer celle-ci.
  closeOnSuccess(event) {
    if (event.target !== this.element) return;
    if (!event.detail?.success) return;

    // `modalController` est exposé sur le nœud de la modale par
    // `modal_controller.js#connect`. Le repli `window.closeModal` couvre le cas
    // où le contrôleur générique n'a pas encore connecté.
    const modal = this.hasModalIdValue ? document.getElementById(this.modalIdValue) : null;
    if (modal?.modalController) {
      modal.modalController.close();
    } else if (modal && typeof window.closeModal === 'function') {
      window.closeModal(this.modalIdValue);
    }
  }

  // Miroir de `DgdsHelper#format_negative_currency` : valeur absolue, signe
  // négatif, et un zéro NON SIGNÉ.
  #formatDeduction(cents) {
    return this.#format(cents === 0 ? 0 : -Math.abs(cents));
  }

  // « 11 000,50 » (copier-coller, espace insécable) doit donner 1100050 et non
  // 11 : on retire TOUS les blancs avant de normaliser la virgule. Même
  // normalisation que `subcontract_cap_controller.js#toCents` et que
  // `Subcontracts::AmountParser.call` côté serveur.
  #toCents(rawValue) {
    const normalized = String(rawValue || '')
      .replace(/\s/g, '')
      .replace(',', '.');
    const parsed = parseFloat(normalized);

    return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
  }

  // Même procédé que `subcontract_cap_controller.js#format`.
  // `value === 0` est vrai pour `-0` : c'est ce qui écrase le « -0,00 € »
  // qu'`Intl` rendrait sinon.
  #format(cents) {
    const value = cents / 100;

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value === 0 ? 0 : value);
  }
}
